// Match Goals - Movement — over/under goals odds drift (Phase 3 → M2 wave 10).
// Thin wrapper over <MovementChart>; selects the goals movement series by scope +
// line and resolves the bookmaker offset + line label. Controls: bookmaker +
// scope + market line. Cross-module highlight sync stays legacy (Phase 4).
//
// The series/labels/offsets now arrive from app-api via `useWidgetData` as one
// `GoalsCardsMovementPayload` envelope `{ series, labels, bkOffsets }` (was the
// getGoalsMovementData / getGoalsMovementLabels / getBkOffsets getters); the
// controls reshape the fetched payload client-side (no refetch).

import { shapeMovement } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import MovementChart from "./MovementChart.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

export interface GoalsMarketMovementProps {
  fixture: string;
  bookmaker?: string | null;
  scope?: string | null;
  /** Market line toggle, e.g. "under_0.5"; defaults to "over_0.5". */
  marketLine?: string | null;
}

export default function GoalsMarketMovement({
  fixture,
  bookmaker,
  scope,
  marketLine,
}: GoalsMarketMovementProps) {
  const fix = fixture || "Arsenal v Everton";
  const { data, isPending, isError, refetch } = useWidgetData(
    "match-goals-movement",
    useFixtureKey(fix)
  );
  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  const key = marketLine || "over_0.5";
  const series = data?.series ?? {};
  const fixData = series[fix] ?? series["Arsenal v Everton"];
  const scopeData = fixData?.[scope || "Match"] ?? fixData?.["Match"];
  const base = scopeData?.[key] ?? scopeData?.["over_0.5"] ?? [];
  const offset = (data?.bkOffsets ?? {})[bookmaker || "Pinnacle"] ?? 0;
  const label = (data?.labels ?? {})[key] ?? key;
  const shape = shapeMovement(base, offset, label);
  if (!shape) return <div className="gmm-wrapper bm-wrapper" />;
  return <MovementChart wrapperClass="gmm-wrapper" shape={shape} />;
}
